import { LogIn, LogOut, Search, User, Users, Heart } from 'lucide-react'
import logoUrl from '#app/assets/logo.svg'
import { FloatingDock, type FloatingDockItem } from './ui/floating-dock'

interface NavigationDockProps {
	/**
	 * Current authenticated user (null if not logged in)
	 */
	user: { uid: string; email: string | null } | null
	/**
	 * Whether the user has created a profile
	 */
	hasProfile: boolean
	/**
	 * User's city name (if they have a profile)
	 */
	userCity?: string
	/**
	 * Callback to open share drawer
	 */
	onShareClick?: (city?: string) => void
}

export function NavigationDock({
	user,
	hasProfile,
	userCity,
	onShareClick,
}: NavigationDockProps) {
	// Build dock items based on auth state
	const dockItems: FloatingDockItem[] = []

	// 1. Logo (always present) - goes to home
	dockItems.push({
		title: 'Home',
		icon: <img src={logoUrl} alt="IndieWorld" className="h-full w-full" />,
		href: '/',
	})

	// 2. Auth-dependent items
	if (!user) {
		// NOT LOGGED IN: Show "Join" icon
		dockItems.push({
			title: 'Join the map',
			icon: <Users className="text-primary h-full w-full" />,
			href: '/auth/twitter',
		})
		// Add dedicated Sign In button
		dockItems.push({
			title: 'Sign In',
			icon: <LogIn className="text-primary h-full w-full" />,
			href: '/auth/twitter',
		})
	} else if (!hasProfile) {
		// LOGGED IN BUT NO PROFILE: Show "Create Profile" icon
		dockItems.push({
			title: 'Create your profile',
			icon: <Users className="text-primary h-full w-full" />,
			href: '/profile/new',
		})
	} else {
		// LOGGED IN WITH PROFILE: Show profile + logout
		dockItems.push({
			title: 'My Profile',
			icon: <User className="text-primary h-full w-full" />,
			href: '/profile',
		})
	}

	// 3. Search (always available)
	dockItems.push({
		title: 'Search',
		icon: <Search className="text-primary h-full w-full" />,
		href: '/search',
	})

	// 4. Share/Heart button (always available)
	if (onShareClick) {
		dockItems.push({
			title: user && hasProfile ? 'Share your journey' : 'Share IndieWorld',
			icon: <Heart className="text-primary h-full w-full" />,
			onClick: () => {
				// Pass user's city if authenticated with profile, empty string otherwise
				onShareClick(user && hasProfile ? userCity : '')
			},
		})
	}

	// 5. Logout (only if logged in)
	if (user) {
		dockItems.push({
			title: 'Sign Out',
			icon: <LogOut className="h-full w-full text-neutral-400" />,
			onClick: () => {
				// Create and submit logout form
				const form = document.createElement('form')
				form.method = 'POST'
				form.action = 'auth/sign-out'
				document.body.appendChild(form)
				form.submit()
			},
		})
	}

	return (
		<div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2">
			<FloatingDock items={dockItems} />
		</div>
	)
}
